export type LocalizedBookTitle = string | Record<string, unknown> | null | undefined

type DisplayLocale = 'ko' | 'en' | 'ja'

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

const readLocalizedTitle = (value: Record<string, unknown>, locale: DisplayLocale): string | null => {
  return (
    normalizeText(value[locale]) ??
    normalizeText(value.en) ??
    normalizeText(value.ko) ??
    normalizeText(value.bookName) ??
    normalizeText(value.name) ??
    null
  )
}

export const getLocalizedBookTitle = (
  bookTitle: LocalizedBookTitle,
  fallback?: string | null,
  locale: DisplayLocale = 'ko',
): string | null => {
  if (!bookTitle) {
    return normalizeText(fallback)
  }

  if (typeof bookTitle === 'string') {
    const rawTitle = normalizeText(bookTitle)

    if (!rawTitle) {
      return normalizeText(fallback)
    }

    try {
      const parsed = JSON.parse(rawTitle) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return readLocalizedTitle(parsed as Record<string, unknown>, locale) ?? normalizeText(fallback) ?? rawTitle
      }
    } catch {
      return rawTitle
    }

    return rawTitle
  }

  return readLocalizedTitle(bookTitle, locale) ?? normalizeText(fallback)
}

export const getCourseDisplayTitle = ({
  bookTitle,
  bookName,
  label,
  name,
  levelName,
  courseLevelName,
  classLevelName,
  fallback,
  locale = 'ko',
}: {
  bookTitle?: LocalizedBookTitle
  bookName?: string | null
  label?: string | null
  name?: string | null
  levelName?: string | null
  courseLevelName?: string | null
  classLevelName?: string | null
  fallback?: string | null
  locale?: DisplayLocale
}): string => {
  return (
    getLocalizedBookTitle(bookTitle, bookName, locale) ??
    normalizeText(bookName) ??
    normalizeText(name) ??
    normalizeText(label) ??
    normalizeText(courseLevelName) ??
    normalizeText(classLevelName) ??
    normalizeText(levelName) ??
    normalizeText(fallback) ??
    ''
  )
}
